import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import InboxIcon from '@mui/icons-material/Inbox';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SourceDetail from './SourceDetail';
import MasterProcedureDialog from './MasterProcedureDialog';
import ThresholdSettingsDialog from './ThresholdSettingsDialog';
import SourceThresholdDialog from './SourceThresholdDialog';

/**
 * Build a tooltip element from the server-provided statusReasons array.
 */
const StatusTooltip = ({ reasons }) => {
  if (!reasons || reasons.length === 0) return 'No status details available';
  if (reasons.length === 1) return reasons[0];
  return (
    <Box sx={{ py: 0.5 }}>
      {reasons.map((r, i) => (
        <Box key={i} sx={{ lineHeight: 1.6 }}>
          {r}
        </Box>
      ))}
    </Box>
  );
};

const CategoryDetail = ({
  categoryKey,
  categoryLabel,
  categoryHex,
  CategoryIcon,
  sources,
  status: STATUS,
  sourceHealth,
  firebaseUser,
  stores,
  startDate,
  endDate,
  selectedStore,
  isPlaceholder,
}) => {
  const [expandedSource, setExpandedSource] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [masterDialogOpen, setMasterDialogOpen] = useState(false);
  const [procedureInfo, setProcedureInfo] = useState({ authorized: false, sources: {} });
  const [runningProcedure, setRunningProcedure] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [trendPopover, setTrendPopover] = useState({ anchorEl: null, sourceKey: null });
  const [sourceThresholdDialog, setSourceThresholdDialog] = useState({ open: false, source: null });

  useEffect(() => {
    let cancelled = false;
    const fetchProcedureInfo = async () => {
      try {
        const token = await firebaseUser.getIdToken(true);
        const res = await axios.get('/hd-api/data-integrity-2/procedure-sources', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setProcedureInfo(res.data);
      } catch {
        // silently ignore — button just won't show
      }
    };
    if (firebaseUser?.getIdToken) fetchProcedureInfo();
    return () => { cancelled = true; };
  }, [categoryKey, firebaseUser]);

  const fmtDate = (d) => {
    if (!d) return '';
    if (d instanceof Date) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    return String(d).split('T')[0];
  };

  const handleRunProcedure = async (e, sourceKey, sourceName) => {
    e.stopPropagation();
    const sd = fmtDate(startDate);
    const ed = fmtDate(endDate);
    if (!window.confirm(`Run the data refresh procedure for ${sourceName} (${sd} to ${ed})?`)) return;
    try {
      setRunningProcedure(sourceKey);
      const token = await firebaseUser.getIdToken(true);
      await axios.post(
        '/hd-api/data-integrity-2/run-procedure',
        { sourceKey, category: categoryKey, startDate: sd, endDate: ed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSnackbar({ open: true, message: 'Procedure started successfully', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to run procedure';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRunningProcedure(null);
    }
  };

  const masterInfo = procedureInfo.masterProcedures?.[categoryKey];

  const handleRunMasterProcedure = async ({ storeId: dialogStoreId, startDate: dialogStart, endDate: dialogEnd }) => {
    const sd = fmtDate(dialogStart);
    const ed = fmtDate(dialogEnd);
    try {
      setRunningProcedure('__master__');
      const token = await firebaseUser.getIdToken(true);
      await axios.post(
        '/hd-api/data-integrity-2/run-procedure',
        { category: categoryKey, startDate: sd, endDate: ed, storeId: dialogStoreId, isMaster: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSnackbar({ open: true, message: 'Master procedure started successfully', severity: 'success' });
      setMasterDialogOpen(false);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to run master procedure';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRunningProcedure(null);
    }
  };

  const hasSources = sources.length > 0;

  const handleToggleSource = (sourceKey) => {
    setExpandedSource((prev) => (prev === sourceKey ? null : sourceKey));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `4px solid ${categoryHex}`,
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          backgroundColor: alpha(categoryHex, 0.04),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {CategoryIcon && (
            <CategoryIcon sx={{ color: categoryHex, fontSize: 22 }} />
          )}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontFamily: 'Lato' }}
          >
            {categoryLabel} Sources
          </Typography>
          <Box sx={{ flex: 1 }} />
          {procedureInfo.authorized && masterInfo && (
            <Tooltip title={masterInfo.label} arrow>
              <IconButton
                size="small"
                onClick={() => setMasterDialogOpen(true)}
                sx={{ color: 'text.secondary' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Threshold Settings" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSettingsOpen(true);
              }}
              sx={{ color: 'text.secondary' }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {!hasSources ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <InboxIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            No data sources configured
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', display: 'block' }}
          >
            This category will populate once data pipelines are connected.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Days Present</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Stores Present</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Freshness</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Trend</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.map((source, index) => {
                const statusInfo = STATUS[source.status] || STATUS.green;
                const isExpanded = expandedSource === source.key;
                const missingCount = source.missingDays?.length ?? 0;

                return (
                  <React.Fragment key={source.key}>
                    <TableRow
                      hover
                      onClick={() => handleToggleSource(source.key)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor:
                          index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                        '& .refresh-btn': { opacity: 0, transition: 'opacity 0.15s' },
                        '&:hover .refresh-btn': { opacity: 1 },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FiberManualRecordIcon
                              sx={{ fontSize: 8, color: statusInfo.color }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {source.name}
                            </Typography>
                            {source.dataLagDays > 0 && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      {source.dataLagDays}-day data lag
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                      Checking data from {source.laggedDateRange?.startDate} to {source.laggedDateRange?.endDate}
                                    </Typography>
                                  </Box>
                                }
                                arrow
                                placement="right"
                              >
                                <HistoryIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            )}
                            {procedureInfo.authorized && procedureInfo.sources[source.key] && (
                              <Tooltip title="Run data refresh procedure" arrow>
                                <IconButton
                                  className="refresh-btn"
                                  size="small"
                                  onClick={(e) => handleRunProcedure(e, source.key, source.name)}
                                  disabled={runningProcedure === source.key}
                                  sx={{ ml: 0.5, p: 0.5, ...(runningProcedure === source.key && { opacity: '1 !important' }) }}
                                >
                                  {runningProcedure === source.key ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <RefreshIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {procedureInfo.authorized && (
                              <Tooltip title={source.hasCustomThresholds ? 'Edit custom thresholds' : 'Configure thresholds'} arrow>
                                <IconButton
                                  className="refresh-btn"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSourceThresholdDialog({ open: true, source });
                                  }}
                                  sx={{
                                    ml: 0.5,
                                    p: 0.5,
                                    ...(source.hasCustomThresholds && {
                                      color: 'primary.main',
                                      opacity: '1 !important',
                                    }),
                                  }}
                                >
                                  <TuneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {source.description && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                              {source.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={<StatusTooltip reasons={source.statusReasons} />}
                          arrow
                          placement="right"
                          enterDelay={0}
                          leaveDelay={200}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: statusInfo.color,
                              letterSpacing: 0.5,
                              cursor: 'help',
                            }}
                          >
                            {statusInfo.label}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {source.presentDays}/{source.totalDays}
                          </Typography>
                          {missingCount > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#c62828',
                                display: 'block',
                                lineHeight: 1.2,
                              }}
                            >
                              {missingCount} missing
                            </Typography>
                          )}
                          {source.storeAnomalies?.length > 0 && (
                            <Tooltip
                              arrow
                              title={
                                <Box sx={{ py: 0.5 }}>
                                  {source.storeAnomalies.slice(0, 10).map((a, i) => (
                                    <Box key={i} sx={{ lineHeight: 1.6 }}>
                                      Store {a.storeId} on {a.date}: {a.rowCount.toLocaleString()} rows ({Math.round((a.rowCount / a.storeMedian) * 100)}% of store's median {a.storeMedian.toLocaleString()})
                                    </Box>
                                  ))}
                                  {source.storeAnomalies.length > 10 && (
                                    <Box sx={{ lineHeight: 1.6, fontStyle: 'italic' }}>
                                      ...and {source.storeAnomalies.length - 10} more
                                    </Box>
                                  )}
                                </Box>
                              }
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#e65100',
                                  display: 'block',
                                  lineHeight: 1.2,
                                  fontWeight: 600,
                                  cursor: 'help',
                                }}
                              >
                                {source.storeAnomalies.length} store{' '}
                                {source.storeAnomalies.length === 1
                                  ? 'anomaly'
                                  : 'anomalies'}
                              </Typography>
                            </Tooltip>
                          )}
                          {source.dayOfWeekAnomalies?.length > 0 && (
                            <Tooltip
                              arrow
                              title={
                                <Box sx={{ py: 0.5 }}>
                                  {source.dayOfWeekAnomalies.map((a, i) => (
                                    <Box key={i} sx={{ lineHeight: 1.6 }}>
                                      {a.dayName} {a.date}: {a.rowCount.toLocaleString()} rows ({a.percentOfExpected}% of {a.dayName} avg {a.expectedAvg.toLocaleString()})
                                    </Box>
                                  ))}
                                </Box>
                              }
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#e65100',
                                  display: 'block',
                                  lineHeight: 1.2,
                                  fontWeight: 600,
                                  cursor: 'help',
                                }}
                              >
                                {source.dayOfWeekAnomalies.length} day-of-week{' '}
                                {source.dayOfWeekAnomalies.length === 1
                                  ? 'anomaly'
                                  : 'anomalies'}
                              </Typography>
                            </Tooltip>
                          )}
                          {source.exceptionCount > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#2e7d32',
                                display: 'block',
                                lineHeight: 1.2,
                              }}
                            >
                              ({source.exceptionCount} exception{source.exceptionCount === 1 ? '' : 's'})
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <span
                          style={{
                            color:
                              source.totalStores > 0 &&
                              source.storesPresent < source.totalStores
                                ? source.storesPresent / source.totalStores < 0.9
                                  ? '#c62828'
                                  : '#f9a825'
                                : 'inherit',
                            fontWeight:
                              source.totalStores > 0 &&
                              source.storesPresent < source.totalStores
                                ? 600
                                : 400,
                          }}
                        >
                          {source.totalStores != null
                            ? `${source.storesPresent}/${source.totalStores}`
                            : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const h = sourceHealth?.[source.key];
                          if (!h || h.daysAgo == null) return <Typography variant="body2" color="text.disabled">—</Typography>;
                          const color = h.daysAgo <= 1 ? 'success' : h.daysAgo <= 3 ? 'warning' : 'error';
                          const label = h.daysAgo === 0 ? 'Today' : h.daysAgo === 1 ? '1d ago' : `${h.daysAgo}d ago`;
                          return (
                            <Tooltip title={`Latest data: ${h.latestDate}`} arrow>
                              <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontWeight: 600, fontSize: 11 }} />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const h = sourceHealth?.[source.key];
                          if (!h || h.trendPct == null) return <Typography variant="body2" color="text.disabled">—</Typography>;
                          const sign = h.trendPct > 0 ? '+' : '';
                          const color = Math.abs(h.trendPct) <= 15 ? 'success' : Math.abs(h.trendPct) <= 30 ? 'warning' : 'error';
                          return (
                            <Chip
                              label={`${sign}${h.trendPct}%`}
                              size="small"
                              color={color}
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: 11, cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); setTrendPopover({ anchorEl: e.currentTarget, sourceKey: source.key }); }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          p: 0,
                          borderBottom: isExpanded ? undefined : 'none',
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <SourceDetail
                            sourceKey={source.key}
                            sourceName={source.name}
                            categoryKey={categoryKey}
                            valueColumnName={source.valueColumnName}
                            valueFormat={source.valueFormat}
                            firebaseUser={firebaseUser}
                            startDate={startDate}
                            endDate={endDate}
                            selectedStore={selectedStore}
                            isPlaceholder={isPlaceholder}
                            sourceSummary={source}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ThresholdSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        categoryKey={categoryKey}
        categoryLabel={categoryLabel}
        firebaseUser={firebaseUser}
      />
      <SourceThresholdDialog
        open={sourceThresholdDialog.open}
        onClose={() => setSourceThresholdDialog({ open: false, source: null })}
        sourceId={sourceThresholdDialog.source?.id}
        sourceName={sourceThresholdDialog.source?.name}
        sourceKey={sourceThresholdDialog.source?.key}
        categoryKey={categoryKey}
        categoryLabel={categoryLabel}
        firebaseUser={firebaseUser}
      />
      {masterInfo && (
        <MasterProcedureDialog
          open={masterDialogOpen}
          onClose={() => setMasterDialogOpen(false)}
          onRun={handleRunMasterProcedure}
          running={runningProcedure === '__master__'}
          stores={stores}
          masterLabel={masterInfo.label}
          defaultStartDate={startDate}
          defaultEndDate={endDate}
          defaultStoreId={selectedStore !== 'all' ? selectedStore : ''}
        />
      )}
      <Popover
        open={!!trendPopover.anchorEl}
        anchorEl={trendPopover.anchorEl}
        onClose={() => setTrendPopover({ anchorEl: null, sourceKey: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {trendPopover.sourceKey && (() => {
          const h = sourceHealth?.[trendPopover.sourceKey];
          if (!h) return null;
          const sign = h.trendPct > 0 ? '+' : '';
          const src = sources.find((s) => s.key === trendPopover.sourceKey);
          return (
            <Box sx={{ p: 2, minWidth: 280 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Lato', fontWeight: 600, mb: 1 }}>
                {src?.name || trendPopover.sourceKey} — Row Count Trend
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>Rows</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(h.priorDays || []).slice().reverse().map((entry) => (
                    <TableRow key={entry.date}>
                      <TableCell sx={{ py: 0.5 }}>{entry.date}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>{entry.count.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                    <TableCell sx={{ py: 0.5, fontWeight: 600 }}>Prior Avg</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 600 }}>
                      {(h.priorAvg ?? 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontWeight: 600 }}>{h.latestDate} (latest)</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 600 }}>
                      {(h.latestCount ?? 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontWeight: 600 }}>Change</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        py: 0.5,
                        fontWeight: 600,
                        color: Math.abs(h.trendPct) <= 15 ? 'success.main' : Math.abs(h.trendPct) <= 30 ? 'warning.main' : 'error.main',
                      }}
                    >
                      {sign}{h.trendPct}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          );
        })()}
      </Popover>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
};

export default CategoryDetail;
